// Integrated home page with embedded game canvas for Solana P2P FPS
import { MenuManager, type MenuSelections } from './menuManager';

export class IntegratedHomePage {
  private container: HTMLElement;
  private gameCanvas: HTMLCanvasElement;
  private gameActive = false;
  private menuManager: MenuManager | null = null;
  private initGameCallback: ((selections: MenuSelections) => void) | null = null;

  constructor() {
    this.gameCanvas = document.getElementById('c') as HTMLCanvasElement;
    this.container = document.createElement('div');
    this.setupIntegratedLayout();
    this.initializeEventListeners();
    this.addAnimations();
  }

  private setupIntegratedLayout(): void {
    // Hide default elements
    const oldHomePage = document.getElementById('home-page');
    const oldMainMenu = document.getElementById('main-menu');
    if (oldHomePage) oldHomePage.style.display = 'none';
    if (oldMainMenu) oldMainMenu.style.display = 'none';

    // Create main container
    this.container.id = 'integrated-container';
    this.container.innerHTML = `
      <!-- Header Bar -->
      <div id="app-header">
        <div class="header-content">
          <div class="logo-section">
            <h1 class="app-logo">BORDERBOXES</h1>
            <span class="app-subtitle">P2P Combat Arena</span>
          </div>
          <div class="header-actions">
            <button id="wallet-connect-btn" class="wallet-btn">
              <span class="wallet-icon">🔗</span>
              Connect Wallet
            </button>
            <div class="player-info" id="player-info" style="display: none;">
              <span class="sol-balance">0.00 SOL</span>
              <span class="player-address">Not Connected</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content Area -->
      <div id="main-content">
        <!-- Left Sidebar -->
        <div id="left-sidebar" class="sidebar">
          <div class="sidebar-section">
            <h3>Game Mode</h3>
            <div class="mode-buttons">
              <button class="mode-btn active" data-mode="practice">Practice</button>
              <button class="mode-btn" data-mode="ranked">Ranked</button>
              <button class="mode-btn" data-mode="tournament">Tournament</button>
            </div>
          </div>
          
          <div class="sidebar-section">
            <h3>Match Settings</h3>
            <div class="settings-controls">
              <label>Map</label>
              <select id="map-select">
                <option value="procedural">Procedural</option>
                <option value="nuketown">Nuketown</option>
                <option value="arena">Arena</option>
              </select>
              
              <label>Difficulty</label>
              <select id="difficulty-select">
                <option value="easy">Easy</option>
                <option value="normal" selected>Normal</option>
                <option value="hard">Hard</option>
              </select>
              
              <label>Players</label>
              <input type="number" id="player-count" value="8" min="2" max="16">
            </div>
          </div>

          <div class="sidebar-section">
            <h3>Your Stats</h3>
            <div class="stats-display">
              <div class="stat-row">
                <span>Wins:</span><span id="stat-wins">0</span>
              </div>
              <div class="stat-row">
                <span>K/D:</span><span id="stat-kd">0.0</span>
              </div>
              <div class="stat-row">
                <span>Rank:</span><span id="stat-rank">Unranked</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Game Container -->
        <div id="game-container">
          <!-- Game Canvas will be moved here -->
          <div id="game-wrapper">
            <!-- Start Screen Overlay - Click anywhere to start -->
            <div id="game-start-overlay" class="game-overlay">
              <div class="overlay-background"></div>
              <div class="start-content">
                <h1 style="color: #ffa500; font-size: 48px; margin-bottom: 20px;">🎮 Borderboxes</h1>
                <h2 style="margin-bottom: 30px;">Click anywhere to start</h2>
                
                <div style="text-align: left; background: rgba(0,0,0,0.5); padding: 20px; border-radius: 10px; margin-bottom: 30px;">
                  <h3 style="color: #ffa500; margin-bottom: 15px;">Controls:</h3>
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 16px;">
                    <div>• <strong>WASD</strong> - Move</div>
                    <div>• <strong>Shift</strong> - Sprint</div>
                    <div>• <strong>Mouse</strong> - Look</div>
                    <div>• <strong>C</strong> - Crouch</div>
                    <div>• <strong>Left Click</strong> - Shoot</div>
                    <div>• <strong>Ctrl</strong> - Slide</div>
                    <div>• <strong>Right Click</strong> - ADS</div>
                    <div>• <strong>E</strong> - Pickup</div>
                    <div>• <strong>1-5</strong> - Weapons</div>
                    <div>• <strong>ESC</strong> - Pause</div>
                  </div>
                </div>
                
                <p style="color: #8899aa; font-style: italic;">Press ESC anytime to release cursor</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Sidebar -->
        <div id="right-sidebar" class="sidebar">
          <div class="sidebar-section">
            <h3>Leaderboard</h3>
            <div id="leaderboard" class="leaderboard-list">
              <div class="leaderboard-item">
                <span class="rank">1</span>
                <span class="player-name">Player1.sol</span>
                <span class="score">2500</span>
              </div>
              <div class="leaderboard-item">
                <span class="rank">2</span>
                <span class="player-name">Gamer2.sol</span>
                <span class="score">2350</span>
              </div>
              <div class="leaderboard-item">
                <span class="rank">3</span>
                <span class="player-name">Pro3.sol</span>
                <span class="score">2100</span>
              </div>
            </div>
          </div>

          <div class="sidebar-section">
            <h3>Active Matches</h3>
            <div id="active-matches" class="match-list">
              <div class="match-item">
                <span class="match-mode">Ranked</span>
                <span class="match-players">6/8</span>
                <button class="join-btn">Join</button>
              </div>
              <div class="match-item">
                <span class="match-mode">Tournament</span>
                <span class="match-players">14/16</span>
                <button class="join-btn">Watch</button>
              </div>
            </div>
          </div>

          <div class="sidebar-section">
            <h3>Chat</h3>
            <div id="chat-container">
              <div id="chat-messages"></div>
              <input type="text" id="chat-input" placeholder="Type a message...">
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div id="app-footer">
        <div class="footer-content">
          <div class="network-status">
            <span class="status-indicator online"></span>
            <span>Solana Mainnet</span>
          </div>
          <div class="game-info">
            <span>FPS: <span id="fps-counter">60</span></span>
            <span>Ping: <span id="ping-counter">12ms</span></span>
            <span>Players Online: <span id="online-counter">0</span></span>
          </div>
        </div>
      </div>
    `;

    document.body.innerHTML = '';
    document.body.appendChild(this.container);
    
    // Move game canvas into the wrapper
    const gameWrapper = document.getElementById('game-wrapper');
    if (gameWrapper && this.gameCanvas) {
      gameWrapper.appendChild(this.gameCanvas);
    }

    this.addStyles();
  }

  private addStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        background: linear-gradient(135deg, #0a0e1a 0%, #1a1f2e 100%);
        color: white;
        overflow: hidden;
        height: 100vh;
        display: flex;
      }

      #integrated-container {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100vh;
      }

      /* Header */
      #app-header {
        background: rgba(20, 25, 40, 0.95);
        border-bottom: 2px solid rgba(255, 165, 0, 0.3);
        padding: 15px 20px;
        backdrop-filter: blur(10px);
      }

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        max-width: 100%;
      }

      .logo-section {
        display: flex;
        align-items: baseline;
        gap: 15px;
      }

      .app-logo {
        font-size: 28px;
        font-weight: 900;
        background: linear-gradient(135deg, #ff6b35 0%, #ffa500 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        letter-spacing: 0.1em;
      }

      .app-subtitle {
        color: #8899aa;
        font-size: 14px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .wallet-btn {
        background: linear-gradient(135deg, #9945ff 0%, #14f195 100%);
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 25px;
        font-weight: 600;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: all 0.3s ease;
      }

      .wallet-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 25px rgba(153, 69, 255, 0.3);
      }

      .wallet-icon {
        font-size: 18px;
      }

      .player-info {
        display: flex;
        align-items: center;
        gap: 15px;
        background: rgba(255, 255, 255, 0.05);
        padding: 8px 15px;
        border-radius: 20px;
        border: 1px solid rgba(255, 255, 255, 0.1);
      }

      .sol-balance {
        color: #14f195;
        font-weight: 600;
      }

      .player-address {
        color: #8899aa;
        font-size: 14px;
        font-family: monospace;
      }

      /* Main Content */
      #main-content {
        display: flex;
        flex: 1;
        overflow: hidden;
      }

      /* Sidebars */
      .sidebar {
        width: 280px;
        background: rgba(20, 25, 40, 0.8);
        border: 1px solid rgba(255, 255, 255, 0.1);
        overflow-y: auto;
        padding: 20px;
      }

      #left-sidebar {
        border-right: 1px solid rgba(255, 255, 255, 0.1);
      }

      #right-sidebar {
        border-left: 1px solid rgba(255, 255, 255, 0.1);
      }

      .sidebar-section {
        margin-bottom: 30px;
      }

      .sidebar-section h3 {
        color: #ffa500;
        font-size: 16px;
        margin-bottom: 15px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .mode-buttons {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .mode-btn {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.2);
        color: white;
        padding: 10px;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.3s ease;
      }

      .mode-btn:hover {
        background: rgba(255, 255, 255, 0.1);
      }

      .mode-btn.active {
        background: rgba(255, 165, 0, 0.2);
        border-color: #ffa500;
        box-shadow: 0 0 15px rgba(255, 165, 0, 0.3);
      }

      .settings-controls {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .settings-controls label {
        color: #8899aa;
        font-size: 14px;
        margin-top: 5px;
      }

      .settings-controls select,
      .settings-controls input {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.2);
        color: white;
        padding: 8px;
        border-radius: 6px;
      }

      .stats-display {
        background: rgba(255, 255, 255, 0.05);
        padding: 15px;
        border-radius: 8px;
      }

      .stat-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;
        color: #b8c5d6;
      }

      /* Game Container */
      #game-container {
        flex: 1;
        position: relative;
        background: #0a0e1a;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      #game-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
      }

      #c {
        width: 100%;
        height: 100%;
        display: block;
      }

      .game-overlay {
        position: absolute;
        inset: 0;
        background: rgba(10, 14, 26, 0.95);
        display: flex;
        align-items: center;
        justify-content: center;
        backdrop-filter: blur(10px);
        z-index: 10;
        cursor: pointer;
      }

      .game-overlay.hidden {
        display: none;
      }
      
      .overlay-background {
        position: absolute;
        inset: 0;
        z-index: 0;
      }

      .start-content {
        text-align: center;
        padding: 40px;
        background: rgba(20, 25, 40, 0.95);
        border-radius: 20px;
        border: 2px solid rgba(255, 165, 0, 0.5);
        max-width: 600px;
        position: relative;
        z-index: 1;
        pointer-events: none;
        box-shadow: 0 0 50px rgba(255, 165, 0, 0.3);
      }

      .start-content h2 {
        font-size: 32px;
        margin-bottom: 15px;
        background: linear-gradient(135deg, #ff6b35 0%, #ffa500 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      .start-content p {
        color: #b8c5d6;
        margin-bottom: 30px;
        font-size: 16px;
      }

      .primary-btn {
        background: linear-gradient(135deg, #ffa500 0%, #ff6b35 100%);
        color: white;
        border: none;
        padding: 15px 40px;
        border-radius: 30px;
        font-size: 18px;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 10px;
        transition: all 0.3s ease;
      }

      .primary-btn:hover {
        transform: translateY(-3px);
        box-shadow: 0 15px 35px rgba(255, 165, 0, 0.4);
      }

      .btn-icon {
        font-size: 24px;
      }

      .quick-actions {
        display: flex;
        gap: 15px;
        margin-top: 20px;
        justify-content: center;
      }

      .secondary-btn {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 165, 0, 0.5);
        color: #ffa500;
        padding: 10px 25px;
        border-radius: 20px;
        cursor: pointer;
        transition: all 0.3s ease;
      }

      .secondary-btn:hover {
        background: rgba(255, 165, 0, 0.1);
        border-color: #ffa500;
      }

      /* Leaderboard */
      .leaderboard-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .leaderboard-item {
        display: flex;
        align-items: center;
        background: rgba(255, 255, 255, 0.05);
        padding: 10px;
        border-radius: 8px;
        gap: 10px;
      }

      .leaderboard-item .rank {
        width: 25px;
        height: 25px;
        background: rgba(255, 165, 0, 0.2);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        color: #ffa500;
        font-size: 12px;
      }

      .leaderboard-item .player-name {
        flex: 1;
        font-size: 14px;
        color: #b8c5d6;
      }

      .leaderboard-item .score {
        color: #14f195;
        font-weight: 600;
        font-size: 14px;
      }

      /* Matches */
      .match-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .match-item {
        display: flex;
        align-items: center;
        background: rgba(255, 255, 255, 0.05);
        padding: 10px;
        border-radius: 8px;
        gap: 10px;
      }

      .match-mode {
        background: rgba(255, 165, 0, 0.2);
        color: #ffa500;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;
      }

      .match-players {
        flex: 1;
        color: #8899aa;
        font-size: 14px;
      }

      .join-btn {
        background: rgba(20, 241, 149, 0.2);
        border: 1px solid #14f195;
        color: #14f195;
        padding: 5px 15px;
        border-radius: 15px;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.3s ease;
      }

      .join-btn:hover {
        background: rgba(20, 241, 149, 0.3);
      }

      /* Chat */
      #chat-container {
        display: flex;
        flex-direction: column;
        height: 200px;
      }

      #chat-messages {
        flex: 1;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 8px;
        padding: 10px;
        overflow-y: auto;
        margin-bottom: 10px;
      }

      #chat-input {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.2);
        color: white;
        padding: 8px;
        border-radius: 20px;
      }

      /* Footer */
      #app-footer {
        background: rgba(20, 25, 40, 0.95);
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        padding: 10px 20px;
      }

      .footer-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .network-status {
        display: flex;
        align-items: center;
        gap: 10px;
        color: #8899aa;
        font-size: 14px;
      }

      .status-indicator {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #ff3333;
      }

      .status-indicator.online {
        background: #14f195;
        animation: pulse 2s infinite;
      }

      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }

      .game-info {
        display: flex;
        gap: 20px;
        color: #8899aa;
        font-size: 14px;
      }

      .game-info span span {
        color: #ffa500;
        font-weight: 600;
      }

      /* Responsive */
      @media (max-width: 1400px) {
        .sidebar {
          width: 240px;
        }
      }

      @media (max-width: 1200px) {
        #right-sidebar {
          display: none;
        }
      }

      @media (max-width: 900px) {
        #left-sidebar {
          position: absolute;
          left: -280px;
          height: 100%;
          z-index: 100;
          transition: left 0.3s ease;
        }

        #left-sidebar.open {
          left: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }

  private initializeEventListeners(): void {
    // Make entire overlay clickable to start game
    const overlay = document.getElementById('game-start-overlay');
    
    // Click anywhere on overlay to start
    overlay?.addEventListener('click', (e) => {
      // Prevent starting if clicking on links or specific elements
      const target = e.target as HTMLElement;
      if (!target.closest('a')) {
        this.startGame();
      }
    });
    
    // Also allow pressing Enter or Space to start
    document.addEventListener('keydown', (e) => {
      if (overlay && !overlay.classList.contains('hidden')) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.startGame();
        }
      }
    });
    
    // ESC key handling is now managed by PauseMenu - removed duplicate handler

    // Wallet connect button
    const walletBtn = document.getElementById('wallet-connect-btn');
    walletBtn?.addEventListener('click', () => {
      this.connectWallet();
    });

    // Mode selection
    const modeButtons = document.querySelectorAll('.mode-btn');
    modeButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        modeButtons.forEach(b => b.classList.remove('active'));
        (e.target as HTMLElement).classList.add('active');
      });
    });

    // Chat input
    const chatInput = document.getElementById('chat-input') as HTMLInputElement;
    chatInput?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.sendChatMessage(chatInput.value);
        chatInput.value = '';
      }
    });
  }

  private startGame(): void {
    const overlay = document.getElementById('game-start-overlay');
    const mapSelect = document.getElementById('map-select') as HTMLSelectElement;
    const difficultySelect = document.getElementById('difficulty-select') as HTMLSelectElement;
    
    // Hide overlay
    overlay?.classList.add('hidden');
    
    // Get selections
    const selections: MenuSelections = {
      map: mapSelect?.value || 'procedural',
      difficulty: difficultySelect?.value as 'easy' | 'normal' | 'hard' || 'normal'
    };

    // Initialize game with selections
    if (this.initGameCallback) {
      this.initGameCallback(selections);
    }

    this.gameActive = true;
    
    // Request pointer lock after a short delay
    setTimeout(() => {
      if (this.gameCanvas) {
        this.gameCanvas.requestPointerLock();
      }
    }, 100);
  }

  private connectWallet(): void {
    // Placeholder for Solana wallet connection
    const walletBtn = document.getElementById('wallet-connect-btn');
    const playerInfo = document.getElementById('player-info');
    
    if (walletBtn && playerInfo) {
      walletBtn.style.display = 'none';
      playerInfo.style.display = 'flex';
      
      // Mock wallet address
      const addressSpan = playerInfo.querySelector('.player-address');
      if (addressSpan) {
        addressSpan.textContent = 'ABC...XYZ';
      }
      
      // Mock balance
      const balanceSpan = playerInfo.querySelector('.sol-balance');
      if (balanceSpan) {
        balanceSpan.textContent = '15.42 SOL';
      }
    }

    console.log('Solana wallet connection would happen here');
  }

  private sendChatMessage(message: string): void {
    if (!message.trim()) return;
    
    const chatMessages = document.getElementById('chat-messages');
    if (chatMessages) {
      const msgElement = document.createElement('div');
      msgElement.style.cssText = 'color: #b8c5d6; margin-bottom: 5px; font-size: 14px;';
      msgElement.innerHTML = `<span style="color: #ffa500;">You:</span> ${message}`;
      chatMessages.appendChild(msgElement);
      chatMessages.scrollTop = chatMessages.scrollHeight;
    }
  }

  private addAnimations(): void {
    // Add floating animation to start content
    const startContent = document.querySelector('.start-content') as HTMLElement;
    if (startContent) {
      startContent.style.animation = 'float 3s ease-in-out infinite';
    }

    // Add floating animation style
    const floatStyle = document.createElement('style');
    floatStyle.textContent = `
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
    `;
    document.head.appendChild(floatStyle);
  }

  public setGameInitCallback(callback: (selections: MenuSelections) => void): void {
    this.initGameCallback = callback;
  }

  public updateOnlineCount(count: number): void {
    const counter = document.getElementById('online-counter');
    if (counter) {
      counter.textContent = count.toString();
    }
  }

  public updateFPS(fps: number): void {
    const counter = document.getElementById('fps-counter');
    if (counter) {
      counter.textContent = fps.toString();
    }
  }
}
